/**
 * Dev / debug flags — gating for review-only affordances.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  2026-05-21 (Anil, item 8) — the Admin/Staff/Client role-preview switcher
 *  is a dev/demo tool: it lets a reviewer walk all three role tracks from
 *  one sign-in. It must NOT appear in the normal production UI — a real
 *  user would see a control that silently changes what the app shows them.
 * ─────────────────────────────────────────────────────────────────────────
 *
 *  `devToolsEnabled()` is the single gate. It is true ONLY when explicitly
 *  opted in — so production is clean by default:
 *
 *    1. a `?dev=1` query param (persisted to localStorage once seen), OR
 *    2. a persisted `avbp.devTools === '1'` localStorage value, OR
 *    3. a non-production build (Vite `import.meta.env.DEV` — i.e. `npm run
 *       dev`), so a developer always has the tool locally.
 *
 *  `?dev=0` clears the persisted opt-in. Pure, no React — unit-testable.
 */

const DEV_KEY = 'avbp.devTools';

/** PURE — decide dev-tools state from the inputs (testable without window). */
export function resolveDevTools(
  queryParam: string | null,
  stored: string | null,
  isDevBuild: boolean,
): boolean {
  if (queryParam === '1') return true;
  if (queryParam === '0') return false;
  if (stored === '1') return true;
  return isDevBuild;
}

/**
 * Is the dev-tools surface (the role-preview switcher) enabled for this
 * session? Reads the `?dev=` query param, the persisted opt-in, and the
 * build mode. Safe to call before React mounts.
 */
export function devToolsEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  const q = new URLSearchParams(window.location.search).get('dev');
  // Persist / clear an explicit query-param choice so it survives reloads.
  if (q === '1' || q === '0') {
    try {
      if (q === '1') window.localStorage.setItem(DEV_KEY, '1');
      else window.localStorage.removeItem(DEV_KEY);
    } catch {
      /* private mode — the query param still wins for this load */
    }
  }

  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(DEV_KEY);
  } catch {
    /* ignore */
  }

  const isDevBuild =
    typeof import.meta !== 'undefined' &&
    Boolean(
      (import.meta as { env?: { DEV?: boolean } }).env?.DEV,
    );

  return resolveDevTools(q, stored, isDevBuild);
}

/* ════════════ Simulate-lead buttons (URL-explicit ONLY) ════════════
 *
 * 2026-07-03 (leads/quotes streamline 1e) — the "Simulate website /
 * free-tool / WhatsApp" demo buttons must NOT sit on the prod Leads screen.
 * Owner incident 2026-07-03 (Compare tab): a demo flag persisted via
 * localStorage made sample data show on a PLAIN url later. Binding rule
 * since: a demo affordance is enabled ONLY by an explicit query param on
 * THIS page-load — no localStorage persistence, no dev-build default.
 */

/** PURE — decide from the raw query param (testable without window). */
export function resolveSimulateLeads(queryParam: string | null): boolean {
  return queryParam === '1';
}

/**
 * Are the Leads screen's "Simulate …" demo buttons enabled for THIS
 * page-load? TRUE only when `?simLeads=1` is literally in the current URL —
 * in ALL builds. Nothing is persisted; a plain URL is always clean.
 */
export function simulateLeadsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return resolveSimulateLeads(
    new URLSearchParams(window.location.search).get('simLeads'),
  );
}
